import { useCallback } from 'react';
import { confirmDialog } from 'primereact/confirmdialog';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '../../contexts/account/AccountContexts';
import useChangeName from '../projects/UseChangeName';
import { AGENCY_HUB } from '../../routes/path';
import { isAdminOrOwner } from '../../utils/utils';
import { promptFitRejection } from '../../helpers/prompt/promptFit';

/**
 * The allocation rule's rejections as the panel's dialogs (needs a mounted `<ConfirmDialog />`). A
 * full allocation leads OWNER / ADMIN to the project's edit dialog (where the reservation is raised)
 * and to the Agency Hub; a USER cannot allocate, so for them it is information only. A project paused
 * in the hub leads OWNER / ADMIN to the hub and tells a USER to ask an administrator. Project names
 * come from the account's project list.
 */
const usePromptFitDialog = ({ onRefresh }: { onRefresh: () => void }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accountResponse, projectResponse } = useAccount();
  const { editName } = useChangeName({ onRefresh });
  const canOpenHub = Boolean(accountResponse && isAdminOrOwner(accountResponse.role));

  const nameOf = useCallback(
    (projectId: number) =>
      projectResponse?.find((project) => project.id === projectId)?.projectLabel ?? '',
    [projectResponse]
  );

  const showAllocationLimit = useCallback(
    (projectId: number, used: number, allocation: number) => {
      confirmDialog({
        header: t('allocationExceededTitle'),
        // Only OWNER / ADMIN can raise the allocation; anyone else is told to ask them.
        message: t(canOpenHub ? 'allocationExceeded' : 'allocationExceededAskAdmin', {
          name: nameOf(projectId),
          used,
          allocation,
        }),
        defaultFocus: 'accept',
        ...(canOpenHub
          ? {
              acceptLabel: t('editProject'),
              // The edit dialog is the same ConfirmDialog element — open it once this one has closed.
              accept: () => setTimeout(() => editName(projectId), 0),
              rejectLabel: t('agencyHub'),
              reject: () => navigate(AGENCY_HUB),
            }
          : { acceptLabel: 'OK', rejectClassName: 'hide' }),
      });
    },
    [t, nameOf, editName, canOpenHub, navigate]
  );

  const showProjectPaused = useCallback(
    (projectId: number) => {
      confirmDialog({
        header: t('projectPausedInHubTitle'),
        message: t(canOpenHub ? 'projectPausedInHub' : 'projectPausedInHubAskAdmin', {
          name: nameOf(projectId),
        }),
        defaultFocus: 'accept',
        acceptLabel: canOpenHub ? t('agencyHub') : 'OK',
        accept: canOpenHub ? () => navigate(AGENCY_HUB) : undefined,
        rejectClassName: 'hide',
      });
    },
    [t, nameOf, canOpenHub, navigate]
  );

  /** The dialog for a typed rejection out of an axios error; false when the error is something else. */
  const showPromptFitError = useCallback(
    (error: unknown): boolean => {
      const rejection = promptFitRejection(error);
      if (!rejection) {
        return false;
      }
      if (rejection.kind === 'ALLOCATION_EXCEEDED') {
        showAllocationLimit(rejection.projectId, rejection.used, rejection.allocation);
      } else {
        showProjectPaused(rejection.projectId);
      }
      return true;
    },
    [showAllocationLimit, showProjectPaused]
  );

  return { showPromptFitError, showAllocationLimit, showProjectPaused };
};

export default usePromptFitDialog;
